/**
 * SalesOnboardingGate — Modal bloquante à la 1re ouverture du module Ventes.
 *
 * Comportement :
 *  - Ouvre le wizard IA en un clic
 *  - OU permet de déclarer « je démarre de 0, pas de facturation antérieure »
 *    (utile pour freelances, nouvelles sociétés — pas de forçage)
 *  - Passe silencieusement si `wizard_completed` déjà true côté backend
 *  - Peut être fermée par escape SEULEMENT après un choix explicite
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Sparkles, Zap, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import AIBillingSetupWizard from "@/components/sales/AIBillingSetupWizard";

export default function SalesOnboardingGate() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("invoices.write");
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [declaring, setDeclaring] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get("/billing/setup-status");
      setStatus(r.data);
      setOpen(!r.data.wizard_completed);
    } catch { /* silent */ }
  };
  useEffect(() => { reload(); }, []);

  const declareNoHistory = async () => {
    setDeclaring(true);
    try {
      await api.post("/billing/declare-no-history", {});
      toast.success("Configuration par défaut activée · La 1re facture aura le format Averdy Agency");
      setOpen(false);
      reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setDeclaring(false); }
  };

  if (!status || status.wizard_completed || !canWrite) {
    return (
      <AIBillingSetupWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onDone={() => { setWizardOpen(false); reload(); }}
      />
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={() => { /* pas de fermeture libre */ }}>
        <DialogContent
          className="max-w-xl"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          data-testid="sales-onboarding-gate">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-600" />
              Configure ta facturation avant de commencer
            </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              La loi française impose une numérotation continue de tes factures.
              Comment veux-tu démarrer sur Averdy Agency ?
            </p>
            <div className="grid grid-cols-1 gap-3">
              <Card
                onClick={() => { setOpen(false); setWizardOpen(true); }}
                className="p-4 cursor-pointer border-2 border-transparent hover:border-purple-400 hover:bg-purple-50/40 transition"
                data-testid="onboarding-existing-btn">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-900">
                      J'ai déjà facturé — importer avec l'IA
                    </p>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Dépose ta dernière facture, on détecte automatiquement
                      ton format, ton IBAN et tes mentions légales.
                    </p>
                  </div>
                </div>
              </Card>
              <Card
                onClick={declaring ? undefined : declareNoHistory}
                className="p-4 cursor-pointer border-2 border-transparent hover:border-emerald-400 hover:bg-emerald-50/40 transition"
                data-testid="onboarding-fresh-btn">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    {declaring
                      ? <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" />
                      : <Zap className="w-5 h-5 text-emerald-600" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-900">
                      Je démarre de zéro — première facture avec Averdy Agency
                    </p>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Pas d'historique de facturation. Averdy Agency génère la
                      numérotation par défaut (FR-{new Date().getFullYear()}-000001).
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AIBillingSetupWizard
        open={wizardOpen}
        onOpenChange={(v) => { setWizardOpen(v); if (!v) reload(); }}
        onDone={() => { setWizardOpen(false); reload(); }}
      />
    </>
  );
}
